import type {ChartTokens} from '@/components/ui/apex-chart'
import type {AgregadoCuestionario, NivelDistribucion} from '@/hooks/bateria/use-bateria'
import {
  dominantNivel,
  enRiesgo,
  enRiesgoPct,
  NIVELES,
  prettifyTaxonomia,
  severidadRank,
  totalPersonas,
} from '@/lib/bateria'

/** `clave` es con la que se navega: ApexCharts entrega índices, nunca datos. */
export type EntradaNivel = {clave: string; label: string; dist: NivelDistribucion}

/** `n` son las personas validadas; el reparto de `agregado` solo cuenta las calificables. */
export type EntradaSegmento = {clave: string; label: string; agregado: AgregadoCuestionario; n: number}

/** Espeja la rampa `.bar-*` / `.stroke-*` de `index.css` en valores que entiende un SVG. */
export function coloresDeNivel(tokens: ChartTokens): Record<string, string> {
  return {
    sin_riesgo: tokens.primary,
    bajo: tokens['tone-lime-fg'],
    medio: tokens['tone-amber-fg'],
    alto: tokens['tone-orange-fg'],
    muy_alto: tokens['tone-red-fg'],
  }
}

export function porcentaje(parte: number, total: number): number {
  return total === 0 ? 0 : Math.round((parte / total) * 100)
}

export function entradasConDatos(
  data: Record<string, NivelDistribucion>,
  labelFor: (clave: string) => string = prettifyTaxonomia
): EntradaNivel[] {
  return Object.entries(data)
    .filter(([, dist]) => dist.n_validos > 0)
    .map(([clave, dist]) => ({clave, label: labelFor(clave), dist}))
}

export function ordenarPeorPrimero(entradas: EntradaNivel[]): EntradaNivel[] {
  return [...entradas].sort((a, b) => {
    const rango = severidadRank(a.dist) - severidadRank(b.dist)
    if (rango !== 0) return rango
    return (b.dist.promedio_transformado ?? 0) - (a.dist.promedio_transformado ?? 0)
  })
}

/** Siempre las cinco series de NIVELES en orden, para que cada color signifique lo mismo;
 * `totales` va aparte porque el apilado al 100% pierde el conteo que pide el tooltip. */
export function apiladoPorNivel(entradas: EntradaNivel[]) {
  const ordenadas = ordenarPeorPrimero(entradas)
  return {
    claves: ordenadas.map((e) => e.clave),
    categorias: ordenadas.map((e) => e.label),
    totales: ordenadas.map((e) => totalPersonas(e.dist)),
    series: NIVELES.map((nivel) => ({
      nivel,
      data: ordenadas.map((e) => e.dist.distribucion[nivel] ?? 0),
    })),
  }
}

export function comparativaPromedio(entradas: EntradaNivel[]) {
  const ordenadas = ordenarPeorPrimero(entradas)
  return {
    claves: ordenadas.map((e) => e.clave),
    categorias: ordenadas.map((e) => e.label),
    niveles: ordenadas.map((e) => dominantNivel(e.dist)),
    personas: ordenadas.map((e) => totalPersonas(e.dist)),
    data: ordenadas.map((e) => Math.round((e.dist.promedio_transformado ?? 0) * 10) / 10),
  }
}

/** `personas` es el `n` validado del segmento, no el calificable. */
export function comparativaSegmentos(segmentos: EntradaSegmento[]) {
  const ordenados = [...segmentos].sort((a, b) => {
    const pct = enRiesgoPct(b.agregado.total_general) - enRiesgoPct(a.agregado.total_general)
    if (pct !== 0) return pct
    return severidadRank(a.agregado.total_general) - severidadRank(b.agregado.total_general)
  })
  return {
    claves: ordenados.map((s) => s.clave),
    categorias: ordenados.map((s) => s.label),
    niveles: ordenados.map((s) => dominantNivel(s.agregado.total_general)),
    personas: ordenados.map((s) => s.n),
    enRiesgo: ordenados.map((s) => enRiesgo(s.agregado.total_general)),
    data: ordenados.map((s) => enRiesgoPct(s.agregado.total_general)),
  }
}
